import AnnouncementBar from '../home/components/AnnouncementBar';
import Navbar from '../home/components/Navbar';
import FooterSection from '../home/components/FooterSection';
import SEOHead from '../../components/feature/SEOHead';
import { SITE_URL } from '../../utils/seo';

const features = [
  { icon: 'ri-cpu-line', title: 'Otomatik Yüksek Kullanılabilirlik', desc: 'Sunucu arızası durumunda VergeOS, etkilenen VM\'leri en iyi düğüme taşımak için dar AI kullanır. Başarısız durum sırasında sorunsuz geçiş ve performans sağlanır.' },
  { icon: 'ri-database-2-line', title: 'Eşsiz Veri Koruması', desc: 'Global satır içi tekilleştirme her zaman etkin olup sıfır performans etkisiyle 4:1 veya daha yüksek oran sağlar; depolama maliyetlerini önemli ölçüde düşürür.' },
  { icon: 'ri-file-copy-line', title: 'Klon Gibi Davranan Anlık Görüntüler', desc: 'Sınırsız anlık görüntü alın, sonsuza kadar saklayın; bağımsız, bağımsız kopyalar olarak 3-2-1 yedekleme kuralını karşılar.' },
  { icon: 'ri-building-4-line', title: 'VDC ile Felaket Kurtarma', desc: 'VDC\'ler ağ dahil tüm ortamı kapsüller; DR sitesine kopyalamak ve asenkron replikasyon kurmak yeterlidir.' },
];

const comparisonData = [
  { feature: 'Kurulum', other: 'Birden fazla paket kurulumu', verge: 'Tek paket kurulumu' },
  { feature: 'Kullanılabilirlik', other: 'Manuel yapılandırma gerekli', verge: 'Otomatik, her zaman açık' },
  { feature: 'Veri Koruması', other: 'Ayrı yedekleme çözümleri', verge: 'Yerleşik tekilleştirme + anlık görüntüler' },
  { feature: 'DR Testi', other: 'Karmaşık, üretimi etkiler', verge: 'İzole VDC ile sorunsuz test' },
  { feature: 'Donanım', other: 'Özel donanım gerekli', verge: 'Emtia donanımı destekler' },
];

const haJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'VergeOS Yüksek Erişilebilirlik',
  applicationCategory: 'BusinessApplication',
  operatingSystem: 'Any',
  description: 'VergeOS ile %99.99 uptime. RF2/RF3 artıklık, otomatik yük devretme ve sıfır kesinti ile iş sürekliliği.',
  url: `${SITE_URL}/high-availability`,
};

export default function HighAvailabilityPage() {
  return (
    <div className="min-h-screen w-full bg-white">
      <SEOHead
        title="Yüksek Erişilebilirlik | %99.99 Uptime | VergeOS"
        description="VergeOS ile %99.99 uptime garantisi. RF2/RF3 artıklık, otomatik yük devretme, sıfır kesinti geçişi ve sürücü/sunucu arızasına karşı tam koruma."
        keywords="yüksek erişilebilirlik, high availability, HA, VergeOS, uptime, otomatik yük devretme, iş sürekliliği"
        canonicalPath="/high-availability"
        jsonLd={haJsonLd}
      />
      <AnnouncementBar />
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative w-full overflow-hidden bg-[#0f1117] py-24 text-center">
          <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'repeating-linear-gradient(45deg,#e8600a 0,#e8600a 1px,transparent 0,transparent 50%)', backgroundSize: '20px 20px' }} />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0f1117]/60 to-[#0f1117]/90" />
          <div className="relative z-10 max-w-4xl mx-auto px-6">
            <div className="inline-block bg-[#e8600a]/20 text-[#e8600a] text-xs font-semibold uppercase tracking-widest px-4 py-1.5 rounded-full mb-6">Özellikler</div>
            <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4 leading-tight">
              Altyapı Genelinde <span className="text-[#e8600a]">Yüksek Kullanılabilirlik</span>,<br />Veri Koruması ve Felaket Kurtarma
            </h1>
            <p className="text-xl text-gray-300 mb-8 font-medium">VergeOS Altyapı Dayanıklılığını Basit ve Ekonomik Kılar</p>
          </div>
        </section>

        {/* Intro */}
        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid md:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-3xl font-bold text-[#1e293b] mb-6">UltraConverged Altyapı ile Tek Paket</h2>
                <p className="text-gray-600 mb-6 leading-relaxed">VergeOS, sanallaştırma, depolama ve ağı tek bir tutarlı işletim ortamında birleştiren UltraConverged Altyapıdır. Kurulacak tek paket — ilk kurduğunuz sanal makine sizindir ve ortam her zaman yüksek kullanılabilir olup her felaketten kurtarabilecektir.</p>
              </div>
              <div className="rounded-xl overflow-hidden">
                <img src="https://public.readdy.ai/ai/img_res/fd2f7462adc02804ab799271830d4ac7.jpg" alt="Yüksek Kullanılabilirlik Karşılaştırması" className="w-full h-full object-top" />
              </div>
            </div>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-20 bg-[#f8fafc]">
          <div className="max-w-6xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-[#1e293b] text-center mb-12">Temel Özellikler</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {features.map(f => (
                <div key={f.title} className="bg-white rounded-lg border border-gray-100 p-6 flex gap-4">
                  <span className="w-10 h-10 flex items-center justify-center bg-[#e8600a]/10 rounded-lg text-[#e8600a] shrink-0"><i className={`${f.icon} text-xl`}></i></span>
                  <div>
                    <h3 className="font-semibold text-[#1e293b] mb-2 text-sm">{f.title}</h3>
                    <p className="text-xs text-gray-600 leading-relaxed">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Data Protection */}
        <section className="py-20 bg-white">
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid md:grid-cols-2 gap-16 items-center">
              <div className="rounded-xl overflow-hidden">
                <img src="https://readdy.ai/api/search-image?query=data%20protection%20deduplication%20diagram%20showing%20VergeOS%20global%20inline%20deduplication%20with%20optimized%20remote%20sync%20and%20immutable%20snapshots%20protecting%20against%20data%20loss%20and%20ransomware%2C%20technical%20architecture%20visualization%20dark%20background%20orange%20circuit%20lines%20professional%20enterprise&width=580&height=400&seq=ha_dataprotect1&orientation=landscape" alt="Veri Koruması" className="w-full h-full object-top" />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-[#1e293b] mb-6">Eşsiz Veri Koruması</h2>
                <p className="text-gray-600 mb-6 leading-relaxed">VergeOS her zaman açık global satır içi tekilleştirme ile müşterilere sıfır performans etkisiyle önemli ölçüde depolama kapasitesi tasarrufu sağlar. Global Satır İçi Tekilleştirme aynı zamanda VergeOS veri koruma yeteneklerini de destekler.</p>
                <p className="text-gray-600 mb-6 leading-relaxed">Uygulamalar veya kullanıcılar VergeOS örneğine veri yazarken, tekilleştirme algoritması benzersizlik için 64K segmentlerde inceler.</p>
                <ul className="space-y-3">
                  {['4:1 veya daha yüksek tekilleştirme oranları', 'Sıfır performans etkisi', 'WAN optimizasyonu ile daha az replikasyon trafiği', 'Değişmez anlık görüntüler ile fidye yazılımı koruması'].map(item => (
                    <li key={item} className="flex items-start gap-2 text-sm text-gray-700">
                      <i className="ri-check-line text-[#e8600a] text-base shrink-0 mt-0.5"></i>{item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* DR with VDCs */}
        <section className="py-20 bg-[#f8fafc]">
          <div className="max-w-6xl mx-auto px-6">
            <div className="grid md:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-3xl font-bold text-[#1e293b] mb-6">Sanal Veri Merkezleri ile Felaket Kurtarmayı Basitleştirin</h2>
                <p className="text-gray-600 mb-6 leading-relaxed">Geleneksel felaket kurtarma çözümleri sanal makineler, ağ ayarları ve depolama yapılandırmalarının karmaşık entegrasyonunu gerektirir. VergeOS bunu VDC'lerle halleder.</p>
                <p className="text-gray-600 mb-6 leading-relaxed">VDC'ler tüm veri merkezini (VM\'ler, ağ ve depolama dahil) tek bir nesne olarak kapsüller. Bu kapsülleme DR sitesine kolay replikasyon ve tek veri merkezi olarak yönetim sağlar.</p>
                <div className="bg-[#0f1117] rounded-lg p-5">
                  <p className="text-white text-sm font-medium mb-2">%100 Başarı Oranı</p>
                  <p className="text-gray-400 text-sm">VergeOS müşterileri hızlı DR testlerinde %100 başarı oranı ve kurtarma sürelerinde önemli azalma bildirmektedir.</p>
                </div>
              </div>
              <div className="rounded-xl overflow-hidden">
                <img src="https://readdy.ai/api/search-image?query=disaster%20recovery%20architecture%20showing%20production%20site%20and%20off-site%20DR%20site%20connected%20through%20WAN%20optimized%20remote%20sync%2C%20VergeOS%20VDC%20containers%20being%20replicated%20from%20on-premise%20to%20DR%20site%2C%20technical%20infrastructure%20diagram%20dark%20background%20professional%20visualization%20orange%20highlights&width=580&height=440&seq=ha_dr1&orientation=landscape" alt="Felaket Kurtarma Mimarisi" className="w-full h-full object-top" />
              </div>
            </div>
          </div>
        </section>

        {/* Comparison Table */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-6">
            <h2 className="text-3xl font-bold text-[#1e293b] text-center mb-12">VergeOS vs. Diğer Hiper Yöneticiler</h2>
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#0f1117] text-white">
                    <th className="px-5 py-3 text-left font-semibold">Özellik</th>
                    <th className="px-5 py-3 text-left font-semibold">Diğer Hiper Yöneticiler</th>
                    <th className="px-5 py-3 text-left font-semibold text-[#e8600a]">VergeOS</th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonData.map((row, i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-5 py-3 font-medium text-[#1e293b]">{row.feature}</td>
                      <td className="px-5 py-3 text-gray-500">{row.other}</td>
                      <td className="px-5 py-3 text-[#1e293b] font-medium">{row.verge}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      </main>
      <FooterSection />
    </div>
  );
}
